import React, { useState, useRef, useEffect } from 'react';
import { FaceSmileIcon } from '@heroicons/react/24/outline';

interface EmojiPickerProps {
  value: string | undefined;
  onChange: (emoji: string) => void;
  placeholder?: string | undefined;
}

const EMOJI_CATEGORIES = [
  {
    name: 'Documentos',
    emojis: ['📄', '📋', '📁', '📂', '📑', '📚', '📖', '📝', '✏️', '🖊️', '🖋️', '📌', '📍', '🔖', '🏷️']
  },
  {
    name: 'Negócios',
    emojis: ['💼', '📊', '📈', '📉', '💰', '💳', '🏢', '🏭', '🏪', '🏨', '🏥', '🏫', '🏛️', '⛪', '🕌']
  },
  {
    name: 'Tecnologia',
    emojis: ['💻', '🖥️', '⌨️', '🖱️', '📱', '📞', '📟', '📠', '🖨️', '📡', '🔌', '🔋', '💡', '🔦', '🕯️']
  },
  {
    name: 'Comunicação',
    emojis: ['📧', '📨', '📩', '📪', '📫', '📬', '📭', '📮', '📯', '📢', '📣', '🔊', '🔉', '🔈', '🔇']
  },
  {
    name: 'Diversos',
    emojis: ['⭐', '🌟', '✨', '💫', '🔥', '💥', '⚡', '💎', '💍', '🎯', '🎪', '🎨', '🎭', '🎬', '🎤']
  }
];

export const EmojiPicker: React.FC<EmojiPickerProps> = ({
  value,
  onChange,
  placeholder = '📄'
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [activeCategory, setActiveCategory] = useState(0);
  const pickerRef = useRef<HTMLDivElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const categoryRefs = useRef<(HTMLDivElement | null)[]>([]);
  const tabsContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  useEffect(() => {
    const handleScroll = () => {
      if (!scrollContainerRef.current) return;

      const container = scrollContainerRef.current;
      const containerRect = container.getBoundingClientRect();
      const containerTop = containerRect.top;
      const containerHeight = containerRect.height;

      // Encontrar qual categoria está mais visível
      let maxVisibility = 0;
      let mostVisibleCategory = 0;

      categoryRefs.current.forEach((categoryRef, index) => {
        if (!categoryRef) return;

        const categoryRect = categoryRef.getBoundingClientRect();
        const categoryTop = categoryRect.top;
        const categoryHeight = categoryRect.height;

        // Calcular quanto da categoria está visível
        const visibleTop = Math.max(categoryTop, containerTop);
        const visibleBottom = Math.min(categoryTop + categoryHeight, containerTop + containerHeight);
        const visibleHeight = Math.max(0, visibleBottom - visibleTop);
        const visibility = visibleHeight / categoryHeight;

        if (visibility > maxVisibility) {
          maxVisibility = visibility;
          mostVisibleCategory = index;
        }
      });

      // Mudar categoria se houver uma mais visível (mesmo com visibilidade baixa)
      if (maxVisibility > 0.1 && mostVisibleCategory !== activeCategory) {
        setActiveCategory(mostVisibleCategory);
        // Forçar scroll horizontal imediatamente
        requestAnimationFrame(() => scrollToActiveTab(mostVisibleCategory));
      }
    };

    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll);
      return () => scrollContainer.removeEventListener('scroll', handleScroll);
    }
  }, [isOpen, activeCategory]);

  const scrollToActiveTab = (categoryIndex: number) => {
    if (!tabsContainerRef.current) return;

    const tabsContainer = tabsContainerRef.current;
    const tabElements = tabsContainer.children;
    const activeTab = tabElements[categoryIndex] as HTMLElement;

    if (activeTab) {
      const containerRect = tabsContainer.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      
      // Calcular se a aba está visível
      const isFullyVisible = tabRect.left >= containerRect.left && tabRect.right <= containerRect.right;
      
      if (!isFullyVisible) {
        // Calcular a posição de scroll para mostrar a aba
        const containerWidth = containerRect.width;
        const tabWidth = tabRect.width;
        const tabLeft = tabRect.left - containerRect.left;
        
        let scrollLeft;
        if (tabRect.left < containerRect.left) {
          // Aba está à esquerda, scroll para a esquerda
          scrollLeft = tabsContainer.scrollLeft + tabLeft - 20;
        } else {
          // Aba está à direita, scroll para a direita
          scrollLeft = tabsContainer.scrollLeft + tabLeft - containerWidth + tabWidth + 20;
        }
        
        // Forçar scroll imediato
        tabsContainer.scrollTo({
          left: Math.max(0, scrollLeft),
          behavior: 'smooth'
        });
      }
    }
  };

  const handleEmojiSelect = (emoji: string) => {
    onChange(emoji);
    setIsOpen(false);
  };

  const handleTabClick = (index: number) => {
    setActiveCategory(index);
    
    // Scroll para a categoria selecionada
    if (categoryRefs.current[index] && scrollContainerRef.current) {
      categoryRefs.current[index]?.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'start' 
      });
    }
  };

  return (
    <div className="relative" ref={pickerRef}>
      <div className="flex">
        <div className="w-12 px-2 py-2 border border-gray-300 rounded-l-md shadow-sm bg-white flex items-center justify-center">
          <span className="text-lg">{value || placeholder}</span>
        </div>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          className="px-2 py-2 border border-l-0 border-gray-300 rounded-r-md bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
        >
          <FaceSmileIcon className="h-4 w-4 text-gray-500" />
        </button>
      </div>

      {isOpen && (
        <div className="fixed inset-0 z-[9999] flex items-start justify-center pt-20">
          <div className="bg-black bg-opacity-25 absolute inset-0" onClick={() => setIsOpen(false)} />
          <div className="relative bg-white border border-gray-200 rounded-lg shadow-xl w-80 max-h-[80vh] overflow-hidden">
            {/* Header */}
            <div className="p-3 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-900">Selecionar Ícone</h3>
            </div>

            {/* Categories - Scrollable tabs */}
            <div className="border-b border-gray-200 bg-gray-50 overflow-x-auto">
              <div className="flex min-w-max" ref={tabsContainerRef}>
                {EMOJI_CATEGORIES.map((category, index) => (
                  <button
                    key={category.name}
                    onClick={() => handleTabClick(index)}
                    className={`flex-shrink-0 px-4 py-2 text-xs font-medium whitespace-nowrap ${
                      activeCategory === index
                        ? 'text-blue-600 border-b-2 border-blue-600 bg-white'
                        : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    {category.name}
                  </button>
                ))}
              </div>
            </div>

            {/* Emojis Grid - Scrollable container */}
            <div 
              ref={scrollContainerRef}
              className="max-h-64 overflow-y-auto"
            >
              {EMOJI_CATEGORIES.map((category, categoryIndex) => (
                <div
                  key={category.name}
                  ref={(el) => (categoryRefs.current[categoryIndex] = el)}
                  className="p-3"
                >
                  <div className="mb-2">
                    <h4 className="text-xs font-medium text-gray-700 mb-2">{category.name}</h4>
                  </div>
                  <div className="grid grid-cols-8 gap-2">
                    {category.emojis.map((emoji, emojiIndex) => (
                      <button
                        key={emojiIndex}
                        onClick={() => handleEmojiSelect(emoji)}
                        className="w-8 h-8 flex items-center justify-center text-lg hover:bg-gray-100 rounded transition-colors"
                        title={emoji}
                      >
                        {emoji}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>

            {/* Footer */}
            <div className="p-3 border-t border-gray-200 bg-gray-50">
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>Role para navegar ou clique em um emoji para selecionar</span>
                <button
                  onClick={() => setIsOpen(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}; 